import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Form, Input, Modal, Segmented, Select, Space, Typography, message, theme } from 'antd';
import {
  Pestania,
  asignar,
  cambiarEstado,
  crearCompra,
  crearVenta,
} from '../../api/oportunidadesApi';
import type { Catalogos, EmpleadoUsuario } from '../../api/oportunidadesApi';
import { INICIATIVA_CLIENTE } from '../../api/emailBienvenidaApi';
import { EmailBienvenidaModal } from './EmailBienvenidaModal';
import { FormularioCompra, FormularioVenta } from './Formularios';
import type { OpcionRemota } from './SelectorRemoto';
import { AVISO_SIN_FICHA, errorOportunidad } from './errores';
import { NOMBRE_PESTANIA, estadoRequiereMotivo, etiquetaEstado, puedeNotificar, servicioAplica } from './reglas';

const { Text } = Typography;

const mostrarError = (error: unknown) => {
  const e = errorOportunidad(error);
  if (e.faltas?.length) {
    Modal.warning({ title: e.mensaje, content: <ul style={{ paddingLeft: 18, margin: 0 }}>{e.faltas.map((f) => <li key={f}>{f}</li>)}</ul> });
  } else {
    message.error(e.mensaje);
  }
};

// ------------------------------------------------------------------ Nueva oportunidad

/**
 * Lo que otra pantalla rellena al abrir «Nueva oportunidad» (Ferias, D-FE-41): los valores del formulario,
 * por el nombre de cada campo, y las etiquetas de los clientes que trae (titulares en la venta, cliente
 * en la compra). Todo sigue siendo editable.
 */
export interface InicialesOportunidad {
  valores: Record<string, unknown>;
  titulares?: OpcionRemota[];
  cliente?: OpcionRemota;
}

interface NuevaOportunidadModalProps {
  abierto: boolean;
  pestaniaInicial: Pestania;
  catalogos: Catalogos;
  yo: EmpleadoUsuario | null;
  onCerrar: () => void;
  onCreada: (pestania: Pestania, id: number) => void;
  /** Valores con los que se abre; sin ellos, el formulario se abre vacío, como siempre. */
  iniciales?: InicialesOportunidad;
  /** El tipo no se puede cambiar: el de `pestaniaInicial`. */
  pestaniaFija?: boolean;
}

/**
 * Alta de los tres tipos (R-06..R-09). La oportunidad nace Pendiente y sin responsable, así que
 * queda pendiente de asignar hasta que la asigne (R-10 a, spec rev. 3). Otra pantalla puede abrirla
 * con valores iniciales y el tipo fijo (Ferias, D-FE-41); vaciar el formulario vuelve a ellos.
 */
export const NuevaOportunidadModal: React.FC<NuevaOportunidadModalProps> = ({
  abierto, pestaniaInicial, catalogos, yo, onCerrar, onCreada, iniciales, pestaniaFija,
}) => {
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const [pestania, setPestania] = useState<Pestania>(pestaniaInicial);
  const [guardando, setGuardando] = useState(false);
  // La venta recién creada cuyo email de bienvenida se enseña (#255); vive aunque este modal se cierre.
  const [bienvenida, setBienvenida] = useState<number | null>(null);

  useEffect(() => {
    if (abierto) {
      setPestania(pestaniaInicial);
      form.resetFields();
    }
  }, [abierto, pestaniaInicial, form]);

  const guardar = async () => {
    const v = await form.validateFields().catch(() => null);
    if (!v) return;
    setGuardando(true);
    try {
      if (pestania === Pestania.Venta) {
        const r = await crearVenta({
          ...v,
          idTipoServicio: servicioAplica(v.idTipoOperacion, catalogos.idTipoOperacionGestion) ? v.idTipoServicio : undefined,
          observaciones: v.observaciones?.trim(),
        });
        message.success(r.sinFarmacia
          ? `Oportunidad de venta nº ${r.idOportunidad} creada sin farmacia identificada.`
          : `Oportunidad de venta nº ${r.idOportunidad} creada.`);
        // A iniciativa del cliente y con algún titular, se ofrece el email de bienvenida (#253, #255).
        if (v.idIniciativa === INICIATIVA_CLIENTE && (v.idsTitulares?.length ?? 0) > 0) setBienvenida(r.idOportunidad);
        onCreada(pestania, r.idOportunidad);
      } else {
        const r = await crearCompra({ ...v, pestania, observaciones: v.observaciones?.trim() });
        message.success(`${pestania === Pestania.Solicitud ? 'Solicitud de información' : 'Oportunidad de compra'} nº ${r.idOportunidad} creada.`);
        onCreada(pestania, r.idOportunidad);
      }
    } catch (error) {
      mostrarError(error);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <Modal
        open={abierto}
        title="Nueva oportunidad"
        width={760}
        onCancel={onCerrar}
        onOk={guardar}
        okText="Guardar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !yo, loading: guardando }}
        destroyOnClose
      >
        {!yo && <Alert type="warning" showIcon message={AVISO_SIN_FICHA} style={{ marginBottom: 16 }} />}
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Segmented
            block
            className="opor-tipo-alta"
            style={{ '--opor-primario': token.colorPrimary } as React.CSSProperties}
            value={pestania}
            disabled={pestaniaFija}
            onChange={(v) => { setPestania(v as Pestania); form.resetFields(); }}
            options={[Pestania.Venta, Pestania.Compra, Pestania.Solicitud].map((p) => ({
              value: p,
              label: p === Pestania.Solicitud ? 'Solicitud de información' : NOMBRE_PESTANIA[p],
            }))}
          />
          {/* resetFields() vuelve a initialValues: a los valores iniciales, o a vacío si no los hay. */}
          <Form form={form} layout="vertical" requiredMark="optional" initialValues={iniciales?.valores}>
            {pestania === Pestania.Venta
              ? <FormularioVenta form={form} catalogos={catalogos} titularesIniciales={iniciales?.titulares} />
              : <FormularioCompra form={form} catalogos={catalogos} pestania={pestania} clienteInicial={iniciales?.cliente} />}
          </Form>
          <Text type="secondary">Nace en estado Pendiente y sin responsable: queda pendiente de asignar hasta que la asignes.</Text>
        </Space>
      </Modal>
      <EmailBienvenidaModal idOportunidad={bienvenida} onCerrar={() => setBienvenida(null)} />
    </>
  );
};

// ------------------------------------------------------------------ Asignar

interface AsignarModalProps {
  objetivo: { pestania: Pestania; id: number; idResponsable?: number } | null;
  catalogos: Catalogos;
  yo: EmpleadoUsuario | null;
  onCerrar: () => void;
  onHecho: () => void;
}

/** Asignar o reasignar a un empleado asignable (R-05, R-10), con aviso por email marcado por defecto (R-11). */
export const AsignarModal: React.FC<AsignarModalProps> = ({ objetivo, catalogos, yo, onCerrar, onHecho }) => {
  const [idResponsable, setIdResponsable] = useState<number>();
  const [notificar, setNotificar] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setIdResponsable(undefined);
    setNotificar(true);
  }, [objetivo]);

  const asignables = catalogos.empleados.filter((e) => e.asignable);
  const ofrecerAviso = puedeNotificar(idResponsable, yo?.idEmpleado);

  const guardar = async () => {
    if (!objetivo || !idResponsable) return;
    setGuardando(true);
    try {
      const r = await asignar(objetivo.pestania, objetivo.id, idResponsable, ofrecerAviso && notificar);
      message.success(r.avisoEncolado ? 'Oportunidad asignada. Se enviará el aviso por email.' : 'Oportunidad asignada.');
      onHecho();
    } catch (error) {
      mostrarError(error);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={!!objetivo}
      title={objetivo ? `Asignar la oportunidad nº ${objetivo.id}` : 'Asignar'}
      onCancel={onCerrar}
      onOk={guardar}
      okText="Asignar"
      cancelText="Cancelar"
      okButtonProps={{ disabled: !yo || !idResponsable, loading: guardando }}
      destroyOnClose
    >
      {!yo && <Alert type="warning" showIcon message={AVISO_SIN_FICHA} style={{ marginBottom: 16 }} />}
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Select
          showSearch
          autoFocus
          optionFilterProp="label"
          placeholder="Elige el responsable"
          aria-label="Responsable"
          value={idResponsable}
          onChange={setIdResponsable}
          options={asignables
            .filter((e) => e.id !== objetivo?.idResponsable)
            .map((e) => ({ value: e.id, label: e.nombre }))}
          style={{ width: '100%' }}
        />
        {ofrecerAviso && (
          <Checkbox checked={notificar} onChange={(e) => setNotificar(e.target.checked)}>
            Notificar al responsable por email
          </Checkbox>
        )}
      </Space>
    </Modal>
  );
};

// ------------------------------------------------------------------ Cambiar estado

interface EstadoModalProps {
  objetivo: { pestania: Pestania; id: number; idEstado: number } | null;
  yo: EmpleadoUsuario | null;
  onCerrar: () => void;
  onHecho: () => void;
}

/** Cualquier estado (R-12); a «Descarte pendiente de confirmar» o «Descartada», motivo obligatorio. */
export const EstadoModal: React.FC<EstadoModalProps> = ({ objetivo, yo, onCerrar, onHecho }) => {
  const [form] = Form.useForm<{ idEstado: number; motivo?: string }>();
  const idEstado = Form.useWatch('idEstado', form);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (objetivo) form.setFieldsValue({ idEstado: objetivo.idEstado, motivo: undefined });
  }, [objetivo, form]);

  const guardar = async () => {
    if (!objetivo) return;
    const v = await form.validateFields().catch(() => null);
    if (!v) return;
    setGuardando(true);
    try {
      await cambiarEstado(objetivo.pestania, objetivo.id, v.idEstado, estadoRequiereMotivo(v.idEstado) ? v.motivo?.trim() : undefined);
      message.success(`Estado cambiado a «${etiquetaEstado(v.idEstado)}».`);
      onHecho();
    } catch (error) {
      mostrarError(error);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={!!objetivo}
      title={objetivo ? `Cambiar el estado de la oportunidad nº ${objetivo.id}` : 'Cambiar estado'}
      onCancel={onCerrar}
      onOk={guardar}
      okText="Guardar"
      cancelText="Cancelar"
      okButtonProps={{ disabled: !yo || idEstado === objetivo?.idEstado, loading: guardando }}
      destroyOnClose
    >
      {!yo && <Alert type="warning" showIcon message={AVISO_SIN_FICHA} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical">
        <Form.Item name="idEstado" label="Estado" rules={[{ required: true }]}>
          <Select options={[1, 2, 3, 4].map((e) => ({ value: e, label: etiquetaEstado(e) }))} />
        </Form.Item>
        {estadoRequiereMotivo(idEstado) && (
          <Form.Item
            name="motivo"
            label="Motivo"
            rules={[{ required: true, whitespace: true, message: 'El motivo es obligatorio para descartar.' }]}
          >
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
